/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getData, putData, postData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

// ─── Status badge helper ──────────────────────────────────────────────────────
// completedpercent: "0"=pending, "1"=in-progress, "3"=completed, "4"=failed
const getStatusBadge = (completedpercent) => {
  switch (completedpercent) {
    case "0": return { color: "bg-secondary", label: "Pending" };
    case "1": return { color: "bg-primary",   label: "In Progress" };
    case "3": return { color: "bg-success",   label: "Completed" };
    case "4": return { color: "bg-danger",    label: "Failed" };
    default:  return { color: "bg-secondary", label: "Pending" };
  }
};

// ─── Payload preview builder ──────────────────────────────────────────────────
// Uses the stored payload_template when available (new format).
// Falls back to a default structure for older records.
const buildPayloadPreview = (automationConfig, isReactive, apiBaseUrl) => {
  if (!automationConfig) return null;

  // New format: payload_template is the full payload object
  if (
    automationConfig.payload_template &&
    typeof automationConfig.payload_template === "object" &&
    Object.keys(automationConfig.payload_template).length > 0
  ) {
    return automationConfig.payload_template;
  }

  // Legacy fallback
  if (isReactive) {
    return {
      callbackUrl: `${apiBaseUrl}/api/project/updatetask/{{taskid}}`,
      callbackmethod: "PUT",
      taskid: "{{taskid}}",
      tasktitle: "{{tasktitle}}",
      taskdescription: "{{taskdescription}}",
      taskpercent: "{{taskpercent}}",
      taskstatus: "{{taskstatus}}",
      email: "{{email}}",
      name: "{{name}}",
      phone: "{{phone}}",
    };
  }
  return { taskid: "{{taskid}}", tasktitle: "{{tasktitle}}", email: "{{email}}", name: "{{name}}", phone: "{{phone}}" };
};

// ─────────────────────────────────────────────────────────────────────────────
// AutomationTaskDetailPopup
// ─────────────────────────────────────────────────────────────────────────────
const AutomationTaskDetailPopup = ({ taskId, isOpen, onClose }) => {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");

  const [isLoading, setIsLoading]       = useState(false);
  const [task, setTask]                 = useState(null);
  const [childTasks, setChildTasks]     = useState([]);
  const [searchQuery, setSearchQuery]   = useState("");
  const [triggering, setTriggering]   = useState(false);
  const [triggerResult, setTriggerResult] = useState(null);
  const [saving, setSaving]           = useState(false);
  const [saveResult, setSaveResult]   = useState(null);
  // Editable automation fields
  const [webhookUrlEditor, setWebhookUrlEditor]       = useState("");
  const [webhookMethodEditor, setWebhookMethodEditor] = useState("POST");
  const [tokenEditor, setTokenEditor]                 = useState("");
  const [payloadEditor, setPayloadEditor]             = useState("");
  const [payloadJsonError, setPayloadJsonError]       = useState("");

  useEffect(() => {
    if (isOpen && taskId) fetchDetails();
  }, [isOpen, taskId]);

  // Sync all editors when automation config loads
  useEffect(() => {
    const cfg = childTasks.find((ct) => ct.automation?.webhook_url)?.automation;
    if (cfg) {
      setWebhookUrlEditor(cfg.webhook_url || "");
      setWebhookMethodEditor(cfg.webhook_method || "POST");
      setTokenEditor(cfg.token || "");
      const tpl = cfg.payload_template;
      setPayloadEditor(tpl ? (typeof tpl === "object" ? JSON.stringify(tpl, null, 2) : tpl) : "");
    }
    setPayloadJsonError("");
    setSaveResult(null);
  }, [childTasks]);

  const fetchDetails = async () => {
    setIsLoading(true);
    setTask(null);
    setChildTasks([]);
    try {
      const resp = await getData(
        `${url.API_BASE_URL}${url.API_GET_AUTOMATION_TASK_SCHEDULE_DETAILS}/${taskId}`
      );
      if (resp.status && resp.data) {
        setTask(resp.data);
        setChildTasks(resp.data.childtask || []);
      }
    } catch (err) {
      console.error("Error fetching task details:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setTask(null);
    setChildTasks([]);
    setSearchQuery("");
    setTriggerResult(null);
    setSaveResult(null);
    setPayloadEditor("");
    setPayloadJsonError("");
    onClose();
  };

  const handlePayloadChange = (value) => {
    setPayloadEditor(value);
    if (value.trim()) {
      try { JSON.parse(value); setPayloadJsonError(""); }
      catch { setPayloadJsonError(t("Invalid JSON format")); }
    } else {
      setPayloadJsonError("");
    }
  };

  const handleSave = async () => {
    if (payloadJsonError || !task?._id) return;
    setSaving(true);
    setSaveResult(null);
    try {
      const data = await putData(
        `${url.API_BASE_URL}/automationtaskschedule/parenttask/${task._id}/payload`,
        {
          webhook_url:      webhookUrlEditor,
          webhook_method:   webhookMethodEditor,
          token:            tokenEditor,
          payload_template: payloadEditor,
        }
      );
      setSaveResult({ success: data?.status, message: data?.message });
    } catch {
      setSaveResult({ success: false, message: t("Network error while saving") });
    } finally {
      setSaving(false);
    }
  };

  const handleTriggerAll = async () => {
    if (!task?._id) return;
    setTriggering(true);
    setTriggerResult(null);
    try {
      const data = await postData(
        `${url.API_BASE_URL}/automationtaskschedule/parenttask/${task._id}/trigger`,
        { callbacktoken: token }
      );
      setTriggerResult({ success: data?.status, message: data?.message });
    } catch {
      setTriggerResult({ success: false, message: "Network error while triggering" });
    } finally {
      setTriggering(false);
    }
  };

  const isReactive = task?.executiontype === "reactive";

  // Automation config is identical on all child tasks – grab the first one that has a URL
  const automationConfig =
    childTasks.find((ct) => ct.automation?.webhook_url)?.automation || null;

  // Proactive API endpoint URL (built once, reused in JSX)
  const proactiveUrl = `${url.API_BASE_URL}/automationtaskschedule/proactive?email=${task?.moderator?.email || "{user_email}"}`;

  const previewJson = (() => {
    const preview = buildPayloadPreview(automationConfig, isReactive, url.API_BASE_URL);
    return preview ? JSON.stringify(preview, null, 2) : null;
  })();

  const filteredChildTasks = childTasks.filter((ct) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      String(ct.customid || "").includes(q) ||
      (ct.leadid?.name    || "").toLowerCase().includes(q) ||
      (ct.leadid?.surname || "").toLowerCase().includes(q) ||
      (ct.leadid?.email   || "").toLowerCase().includes(q) ||
      (ct.leadid?.company || "").toLowerCase().includes(q)
    );
  });

  if (!isOpen) return null;

  return (
    <>
      {/* ── Offcanvas panel ── */}
      <div
        className="offcanvas offcanvas-end bg-white border-0 shadow-lg show d-flex flex-column"
        style={{ visibility: "visible", width: "80vw", maxWidth: "960px" }}
        tabIndex="-1"
        aria-labelledby="offcanvasTaskDetailLabel"
      >
        {/* ── Header ── */}
        <div className="offcanvas-header bg-primary text-white p-4 border-bottom">
          <div className="d-flex align-items-center gap-3">
            <div
              className="p-2 rounded-circle d-flex align-items-center justify-content-center"
              style={{ background: "rgba(255,255,255,0.15)" }}
            >
              <span className="material-symbols-outlined text-white" style={{ fontSize: "22px" }}>
                task_alt
              </span>
            </div>
            <div>
              <h5
                className="offcanvas-title fw-bold text-white mb-1"
                id="offcanvasTaskDetailLabel"
              >
                {task?.title || t("Task Details")}
              </h5>
              <div className="d-flex align-items-center gap-2">
                {task?.executiontype && (
                  <span
                    className={`badge ${
                      isReactive ? "bg-info text-dark" : "bg-warning text-dark"
                    }`}
                  >
                    {isReactive ? t("Reactive") : t("Proactive")}
                  </span>
                )}
                <small className="text-white opacity-75">
                  {childTasks.length} {t("subtasks")}
                </small>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="btn-close btn-close-white"
            onClick={handleClose}
            aria-label="Close"
          />
        </div>

        {/* ── Body ── */}
        <div className="offcanvas-body p-4 flex-grow-1" style={{ overflowY: "auto" }}>
          {isLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t("Loading...")}</span>
              </div>
              <p className="mt-3 text-muted">{t("Loading task details...")}</p>
            </div>
          ) : task ? (
            <div>

              {/* ── 1. Parent Task Info ── */}
              <div className="mb-4 p-4 rounded-3 border border-primary border-opacity-25 shadow-sm bg-light">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="material-symbols-outlined text-primary" style={{ fontSize: "20px" }}>
                    account_tree
                  </span>
                  <h6 className="mb-0 fw-bold text-dark">{t("Parent Task")}</h6>
                </div>

                <h5 className="fw-bold text-dark mb-2">{task.title}</h5>
                {task.description && (
                  <p className="text-muted mb-3" style={{ fontSize: "14px", lineHeight: "1.6" }}>
                    {task.description}
                  </p>
                )}

                <div className="row g-3">
                  <div className="col-md-3 col-6">
                    <small className="text-muted d-flex align-items-center gap-1 mb-1">
                      <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>bolt</span>
                      {t("Execution Type")}
                    </small>
                    <span className={`badge ${isReactive ? "bg-info text-dark" : "bg-warning text-dark"}`}>
                      {isReactive ? t("Reactive") : t("Proactive")}
                    </span>
                  </div>

                  <div className="col-md-3 col-6">
                    <small className="text-muted d-flex align-items-center gap-1 mb-1">
                      <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>list_alt</span>
                      {t("Subtasks")}
                    </small>
                    <span className="badge bg-primary">{childTasks.length} {t("tasks")}</span>
                  </div>

                  {task.moderator && (
                    <div className="col-md-3 col-6">
                      <small className="text-muted d-flex align-items-center gap-1 mb-1">
                        <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>person</span>
                        {t("Moderator")}
                      </small>
                      <span className="fw-semibold text-dark" style={{ fontSize: "13px" }}>
                        {task.moderator.name} {task.moderator.surname}
                      </span>
                    </div>
                  )}

                  <div className="col-md-3 col-6">
                    <small className="text-muted d-flex align-items-center gap-1 mb-1">
                      <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>calendar_today</span>
                      {t("Created")}
                    </small>
                    <span className="fw-semibold text-dark" style={{ fontSize: "13px" }}>
                      {new Date(task.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* ── 2. Automation Config ── */}
              <div className="mb-4 border rounded-3 overflow-hidden shadow-sm">
                <div
                  className="px-4 py-3 border-bottom d-flex align-items-center gap-2"
                  style={{ background: isReactive ? "rgba(13,202,240,0.08)" : "rgba(255,193,7,0.08)" }}
                >
                  <span className="material-symbols-outlined text-secondary" style={{ fontSize: "20px" }}>
                    settings_ethernet
                  </span>
                  <h6 className="mb-0 fw-semibold text-dark">
                    {isReactive ? t("Webhook Config") : t("Payload Config")}
                  </h6>
                  <small className="text-muted">
                    {isReactive
                      ? `— ${t("payload our server sends to n8n")}`
                      : `— ${t("data our API returns to n8n's CRON")}`}
                  </small>
                  <span
                    className={`badge ms-auto ${isReactive ? "bg-info text-dark" : "bg-warning text-dark"}`}
                    style={{ fontSize: "10px" }}
                  >
                    {isReactive ? (automationConfig?.webhook_method || "POST") : "GET"}
                  </span>
                </div>

                <div className="row g-0">
                  {/* Left: config fields */}
                  <div className="col-xl-6 p-4 border-end">
                    {isReactive && automationConfig ? (
                      <>
                        {/* Webhook URL */}
                        <div className="mb-3">
                          <label className="form-label fs-sm fw-semibold text-dark mb-1 d-flex align-items-center gap-1">
                            <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>link</span>
                            {t("Webhook URL")}
                          </label>
                          <input
                            type="url"
                            className="form-control form-control-sm"
                            value={webhookUrlEditor}
                            onChange={(e) => setWebhookUrlEditor(e.target.value)}
                            placeholder="https://your-n8n-instance.com/webhook/..."
                            style={{ fontSize: "12px" }}
                          />
                          <small className="text-muted">{t("The n8n workflow webhook that receives the payload.")}</small>
                        </div>

                        {/* Method */}
                        <div className="mb-3">
                          <label className="form-label fs-sm fw-semibold text-dark mb-1 d-flex align-items-center gap-1">
                            <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>http</span>
                            {t("Method")}
                          </label>
                          <select
                            className="form-select form-select-sm"
                            value={webhookMethodEditor}
                            onChange={(e) => setWebhookMethodEditor(e.target.value)}
                          >
                            <option value="POST">POST</option>
                            <option value="PUT">PUT</option>
                            <option value="PATCH">PATCH</option>
                            <option value="GET">GET</option>
                          </select>
                        </div>

                        {/* Token */}
                        <div className="mb-0">
                          <label className="form-label fs-sm fw-semibold text-dark mb-1 d-flex align-items-center gap-1">
                            <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>key</span>
                            {t("Token")}
                            <span className="badge bg-secondary ms-1" style={{ fontSize: "9px" }}>{t("n8n auth")}</span>
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-sm font-monospace"
                            value={tokenEditor}
                            onChange={(e) => setTokenEditor(e.target.value)}
                            placeholder={t("Leave blank if not required")}
                            style={{ fontSize: "11px" }}
                          />
                          <small className="text-muted">
                            {t("Sent as")} <code>Authorization: Bearer &lt;token&gt;</code> {t("so n8n can verify the request.")}
                          </small>
                        </div>

                        {/* Callback Response Instructions */}
                        <div className="mt-3 p-2 rounded-2 border border-success border-opacity-25" style={{ background: "#f0f9ff" }}>
                          <div className="d-flex align-items-center gap-1 mb-2">
                            <span className="material-symbols-outlined text-success" style={{ fontSize: "14px" }}>send</span>
                            <small className="fw-bold text-dark">{t("Send Response")}</small>
                            <span className="badge bg-success ms-auto" style={{ fontSize: "8px" }}>POST</span>
                          </div>
                          <small className="text-muted d-block mb-2" style={{ fontSize: "10px" }}>
                            {t("When automation completes, POST to:")}
                          </small>
                          <code
                            className="d-block p-2 rounded-2 border bg-white"
                            style={{ fontSize: "10px", wordBreak: "break-all", lineHeight: "1.5" }}
                          >
                            {url.API_BASE_URL}/automationtaskschedule/callback
                          </code>
                          <small className="text-muted d-block mt-2" style={{ fontSize: "10px" }}>
                            <strong>{t("Auth")}:</strong> <code>Authorization: Bearer {`{{token}}`}</code><br/>
                            <strong>{t("Required")}:</strong> <code>taskid</code>, <code>response</code><br/>
                            <strong>{t("Optional")}:</strong> <code>status</code>, <code>progress</code>, <code>executionid</code>
                          </small>
                        </div>
                      </>
                    ) : !isReactive ? (
                      /* Proactive: show copyable API endpoint */
                      <div className="p-3 rounded-2 border" style={{ background: "#fff8e1" }}>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <span className="material-symbols-outlined text-warning" style={{ fontSize: "18px" }}>api</span>
                          <p className="mb-0 fw-semibold text-dark" style={{ fontSize: "13px" }}>
                            {t("n8n CRON polls this endpoint to get pending tasks:")}
                          </p>
                          <span className="badge bg-warning text-dark ms-auto" style={{ fontSize: "10px" }}>GET</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <code
                            className="flex-grow-1 p-2 rounded-2 border"
                            style={{
                              background: "#1e1e2e",
                              color: "#cdd6f4",
                              fontSize: "12px",
                              wordBreak: "break-all",
                              display: "block",
                              lineHeight: "1.8",
                            }}
                          >
                            {proactiveUrl}
                          </code>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary flex-shrink-0"
                            title={t("Copy URL")}
                            onClick={() => navigator.clipboard?.writeText(proactiveUrl)}
                          >
                            <span className="material-symbols-outlined" style={{ fontSize: "18px" }}>
                              content_copy
                            </span>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-muted fst-italic" style={{ fontSize: "13px" }}>
                        {t("No automation config found.")}
                      </p>
                    )}

                  </div>

                  {/* Right: editable payload */}
                  <div className="col-xl-6 p-4 d-flex flex-column" style={{ background: "#fafafa" }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <span className="material-symbols-outlined text-primary" style={{ fontSize: "16px" }}>edit_note</span>
                      <span className="fw-semibold text-dark" style={{ fontSize: "13px" }}>{t("Payload")}</span>
                      <span className={`badge ms-1 ${isReactive ? "bg-info text-dark" : "bg-primary"}`} style={{ fontSize: "10px" }}>
                        {isReactive ? (automationConfig?.webhook_method || "POST") : "GET"}
                      </span>
                    </div>

                    <textarea
                      className={`form-control font-monospace flex-grow-1 ${payloadJsonError ? "is-invalid" : ""}`}
                      rows="12"
                      value={payloadEditor}
                      onChange={(e) => handlePayloadChange(e.target.value)}
                      placeholder={t("No payload configured")}
                      style={{ fontSize: "11.5px", lineHeight: "1.6", resize: "none", minHeight: "240px" }}
                    />
                    {payloadJsonError && <div className="invalid-feedback d-block">{payloadJsonError}</div>}

                    <small className="text-muted d-block mt-2">
                      <span className="material-symbols-outlined align-middle me-1" style={{ fontSize: "13px" }}>info</span>
                      {t("Placeholders replaced at trigger time:")}
                      <ul className="mb-0 mt-1 ps-3" style={{ listStyle: "disc" }}>
                        <li><code>{"{{taskid}}"}</code> — {t("unique task ID")}</li>
                        <li><code>{"{{tasktitle}}"}</code> — {t("task title")}</li>
                        <li><code>{"{{taskdescription}}"}</code> — {t("task description")}</li>
                        <li><code>{"{{taskpercent}}"}</code> — {t("0–100 (completion %)")}</li>
                        <li><code>{"{{taskstatus}}"}</code> — {t("1=inprogress, 2=testing, 3=completed")}</li>
                        <li><code>{"{{email}}"}</code> — {t("lead email")}</li>
                        <li><code>{"{{name}}"}</code> — {t("lead full name")}</li>
                        <li><code>{"{{phone}}"}</code> — {t("lead phone")}</li>
                      </ul>
                      <small className="text-warning d-block mt-1">
                        <span className="material-symbols-outlined align-middle me-1" style={{ fontSize: "12px" }}>warning</span>
                        {t("Saving updates the payload for all subtasks of this automation.")}
                      </small>
                    </small>
                  </div>
                </div>
              </div>

              {/* ── Callback Response Info (Accordion) ── */}
              <div className="accordion mb-4" id="accordionCallbackResponse">
                <div className="accordion-item border border-success border-opacity-25 rounded-3 overflow-hidden shadow-sm">
                  <h2 className="accordion-header" id="headingCallbackResponse">
                    <button
                      className="accordion-button collapsed d-flex align-items-center gap-2 py-3"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseCallbackResponse"
                      aria-expanded="false"
                      aria-controls="collapseCallbackResponse"
                      style={{ background: "#f0f9ff" }}
                    >
                      <span className="material-symbols-outlined text-success" style={{ fontSize: "20px" }}>
                        send
                      </span>
                      <h6 className="mb-0 fw-bold text-dark">{t("Automation Callback Response")}</h6>
                      <span className="badge bg-success ms-auto" style={{ fontSize: "10px" }}>POST</span>
                    </button>
                  </h2>
                  <div
                    id="collapseCallbackResponse"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingCallbackResponse"
                    data-bs-parent="#accordionCallbackResponse"
                  >
                    <div className="accordion-body p-4" style={{ background: "#f0f9ff" }}>
                      <p className="text-muted mb-2" style={{ fontSize: "13px" }}>
                        {t("When your automation (n8n, Retell, etc.) completes, send a POST request to this endpoint to save the response and update the task:")}
                      </p>
                
                <div className="d-flex align-items-center gap-2 mb-3">
                  <code
                    className="flex-grow-1 p-2 rounded-2 border bg-white"
                    style={{
                      fontSize: "11px",
                      wordBreak: "break-all",
                      display: "block",
                      lineHeight: "1.6",
                    }}
                  >
                    {url.API_BASE_URL}/automationtaskschedule/callback
                  </code>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary flex-shrink-0"
                    title={t("Copy URL")}
                    onClick={() => navigator.clipboard?.writeText(`${url.API_BASE_URL}/automationtaskschedule/callback`)}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: "16px" }}>
                      content_copy
                    </span>
                  </button>
                </div>

                <div className="alert alert-info mb-0 p-3" style={{ fontSize: "12px" }}>
                  <div className="fw-semibold mb-2">{t("Sample Request Body:")}</div>
                  <pre
                    className="mb-2 p-2 rounded-2 border bg-white font-monospace"
                    style={{ fontSize: "11px", lineHeight: "1.6", overflowX: "auto" }}
                  >
{`{
  "taskid": "${childTasks[0]?._id || '{{taskid}}'}",
  "response": {
    "result": "Email sent successfully",
    "recipient": "user@example.com"
  },
  "status": "completed",
  "progress": 100,
  "executionid": "n8n-exec-123",
  "source": "n8n"
}`}
                  </pre>
                  <div className="mt-2">
                    <strong>{t("Required fields:")}</strong>
                    <ul className="mb-2 ps-3">
                      <li><code>taskid</code> — {t("Task ID from payload")}</li>
                      <li><code>response</code> — {t("Any object with automation results")}</li>
                    </ul>
                    <strong>{t("Optional fields to update task:")}</strong>
                    <ul className="mb-0 ps-3">
                      <li><code>status</code> — "completed"/"success" (=3), "failed"/"error" (=4), "in-progress" (=1), "pending" (=0)</li>
                      <li><code>progress</code> — {t("Number 0-100 for task completion %")}</li>
                      <li><code>executionid</code> — {t("External workflow ID")}</li>
                      <li><code>source</code> — "n8n", "retell", "webhook", etc.</li>
                    </ul>
                  </div>
                </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Automation Response History ── */}
              {task?.automationResponses && task.automationResponses.length === 0 ? (
                <div className="mb-4 p-4 rounded-3 border border-secondary border-opacity-25 shadow-sm bg-light text-center">
                  <span className="material-symbols-outlined text-secondary mb-2" style={{ fontSize: "32px", opacity: 0.5 }}>
                    inbox
                  </span>
                  <p className="text-muted mb-0 fs-sm">{t("No response received yet")}</p>
                  <small className="text-muted" style={{ fontSize: "11px" }}>{t("Responses from automation workflows will appear here")}</small>
                </div>
              ) : task?.automationResponses && task.automationResponses.length > 0 ? (
                <div className="mb-4 p-4 rounded-3 border border-info border-opacity-25 shadow-sm bg-light">
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <span className="material-symbols-outlined text-info" style={{ fontSize: "20px" }}>
                      history
                    </span>
                    <h6 className="mb-0 fw-bold text-dark">{t("Response History")}</h6>
                    <span className="badge bg-info ms-auto">
                      {task.automationResponses.length} {t("responses")}
                    </span>
                  </div>
                  
                  <div className="d-flex flex-column gap-3">
                    {task.automationResponses.map((resp, idx) => (
                      <div key={resp._id || idx} className="p-3 rounded-3 border bg-white shadow-sm">
                        <div className="d-flex align-items-start gap-3 mb-2">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <span className={`badge ${
                                resp.status === 'success' ? 'bg-success' :
                                resp.status === 'failed' ? 'bg-danger' :
                                resp.status === 'partial' ? 'bg-warning text-dark' :
                                'bg-secondary'
                              }`}>
                                {resp.status || 'pending'}
                              </span>
                              {resp.source && (
                                <span className="badge bg-secondary">{resp.source}</span>
                              )}
                              {resp.executionid && (
                                <small className="text-muted font-monospace" style={{ fontSize: "10px" }}>
                                  ID: {resp.executionid}
                                </small>
                              )}
                            </div>
                            <small className="text-muted d-flex align-items-center gap-1">
                              <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>schedule</span>
                              {new Date(resp.responsetime || resp.createdAt).toLocaleString()}
                            </small>
                          </div>
                        </div>
                        
                        <div className="mt-2">
                          <label className="form-label fs-sm fw-semibold text-dark mb-1">{t("Response Data:")}</label>
                          <pre
                            className="mb-0 p-2 rounded-2 border bg-light font-monospace"
                            style={{
                              fontSize: "11px",
                              lineHeight: "1.5",
                              maxHeight: "200px",
                              overflowY: "auto",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-all"
                            }}
                          >
                            {typeof resp.response === 'object'
                              ? JSON.stringify(resp.response, null, 2)
                              : resp.response}
                          </pre>
                        </div>
                        
                        {resp.error && (
                          <div className="mt-2 alert alert-danger mb-0 p-2" style={{ fontSize: "12px" }}>
                            <strong>{t("Error")}:</strong> {resp.error}
                          </div>
                        )}
                        
                        {resp.notes && (
                          <div className="mt-2">
                            <small className="text-muted">
                              <strong>{t("Notes")}:</strong> {resp.notes}
                            </small>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <div>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="material-symbols-outlined text-primary" style={{ fontSize: "20px" }}>
                    group
                  </span>
                  <h6 className="mb-0 fw-bold text-dark">{t("Leads / Subtasks")}</h6>
                  <span className="badge bg-secondary ms-auto">
                    {childTasks.length} {t("total")}
                  </span>
                </div>

                {/* Search */}
                <div className="mb-3">
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0">
                      <span className="material-symbols-outlined text-muted" style={{ fontSize: "18px" }}>
                        search
                      </span>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0 ps-0"
                      placeholder={t("Search by lead name, email, or company...")}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      style={{ fontSize: "14px" }}
                    />
                    {searchQuery && (
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => setSearchQuery("")}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: "16px" }}>close</span>
                      </button>
                    )}
                  </div>
                  {searchQuery && (
                    <small className="text-muted mt-1 d-block">
                      {filteredChildTasks.length} {t("of")} {childTasks.length} {t("tasks")}
                    </small>
                  )}
                </div>

                {/* Table */}
                <div className="rounded-3 border overflow-hidden shadow-sm">
                  <table className="table table-hover align-middle mb-0">
                    <thead>
                      <tr style={{ background: "#f8f9fa" }}>
                        <th style={{ fontSize: "12px", fontWeight: "600", padding: "11px 14px", color: "#495057" }}>
                          #
                        </th>
                        <th style={{ fontSize: "12px", fontWeight: "600", padding: "11px 14px", color: "#495057", minWidth: "200px" }}>
                          {t("Lead")}
                        </th>
                        <th style={{ fontSize: "12px", fontWeight: "600", padding: "11px 14px", color: "#495057" }}>
                          {t("Schedule Date")}
                        </th>
                        <th style={{ fontSize: "12px", fontWeight: "600", padding: "11px 14px", color: "#495057" }}>
                          {t("Schedule Time")}
                        </th>
                        <th style={{ fontSize: "12px", fontWeight: "600", padding: "11px 14px", color: "#495057" }}>
                          {t("Status")}
                        </th>
                        {isReactive && (
                          <th style={{ fontSize: "12px", fontWeight: "600", padding: "11px 14px", color: "#495057" }}>
                            {t("n8n Execution ID")}
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredChildTasks.length === 0 ? (
                        <tr>
                          <td
                            colSpan={isReactive ? 6 : 5}
                            className="text-center py-5"
                          >
                            <span className="material-symbols-outlined text-muted d-block" style={{ fontSize: "40px" }}>
                              inbox
                            </span>
                            <span className="text-muted" style={{ fontSize: "14px" }}>
                              {searchQuery ? t("No tasks match your search") : t("No subtasks found")}
                            </span>
                          </td>
                        </tr>
                      ) : (
                        filteredChildTasks.map((ct, idx) => {
                          const badge = getStatusBadge(ct.completedpercent);
                          return (
                            <tr key={ct._id || idx}>
                              <td style={{ padding: "11px 14px" }}>
                                <span
                                  className="badge bg-light text-dark border"
                                  style={{ fontSize: "11px", fontWeight: "600" }}
                                >
                                  {ct.customid || idx + 1}
                                </span>
                              </td>

                              <td style={{ padding: "11px 14px" }}>
                                {ct.leadid ? (
                                  <div>
                                    <div className="fw-semibold text-dark" style={{ fontSize: "13px" }}>
                                      {ct.leadid.name} {ct.leadid.surname}
                                    </div>
                                    {ct.leadid.email && (
                                      <div className="text-muted d-flex align-items-center gap-1" style={{ fontSize: "12px" }}>
                                        <span className="material-symbols-outlined" style={{ fontSize: "12px" }}>mail</span>
                                        {ct.leadid.email}
                                      </div>
                                    )}
                                    {ct.leadid.company && (
                                      <div className="text-secondary d-flex align-items-center gap-1" style={{ fontSize: "12px" }}>
                                        <span className="material-symbols-outlined" style={{ fontSize: "12px" }}>business</span>
                                        {ct.leadid.company}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>

                              <td style={{ padding: "11px 14px" }}>
                                <div className="d-flex align-items-center gap-1">
                                  <span className="material-symbols-outlined text-muted" style={{ fontSize: "14px" }}>
                                    calendar_today
                                  </span>
                                  <span style={{ fontSize: "13px" }}>
                                    {ct.startdate || "—"}
                                  </span>
                                </div>
                              </td>

                              <td style={{ padding: "11px 14px" }}>
                                <div className="d-flex align-items-center gap-1">
                                  <span className="material-symbols-outlined text-muted" style={{ fontSize: "14px" }}>
                                    schedule
                                  </span>
                                  <span style={{ fontSize: "13px" }}>
                                    {ct.starttime || "—"}
                                  </span>
                                </div>
                              </td>

                              <td style={{ padding: "11px 14px" }}>
                                <span className={`badge ${badge.color}`} style={{ fontSize: "11px" }}>
                                  {t(badge.label)}
                                </span>
                              </td>

                              {isReactive && (
                                <td style={{ padding: "11px 14px" }}>
                                  {ct.automation?.n8n_execution_id ? (
                                    <code style={{ fontSize: "11px", color: "#6c757d" }}>
                                      {ct.automation.n8n_execution_id}
                                    </code>
                                  ) : (
                                    <span className="text-muted">—</span>
                                  )}
                                </td>
                              )}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

            </div>
          ) : (
            <div className="text-center py-5">
              <span className="material-symbols-outlined text-muted" style={{ fontSize: "64px" }}>
                error_outline
              </span>
              <p className="text-muted mt-3">{t("Failed to load task details")}</p>
              <button className="btn btn-outline-primary btn-sm" onClick={fetchDetails}>
                {t("Retry")}
              </button>
            </div>
          )}
        </div>

        {/* ── Footer: fixed at the bottom of the offcanvas ── */}
        <div className="border-top bg-white px-4 py-3 d-flex align-items-center gap-3 flex-shrink-0">
          {/* Trigger Now */}
          <button
            type="button"
            className="btn btn-success d-flex align-items-center gap-2"
            disabled={triggering || !automationConfig?.webhook_url || !task}
            onClick={handleTriggerAll}
          >
            {triggering ? (
              <><span className="spinner-border spinner-border-sm" /> {t("Triggering…")}</>
            ) : (
              <><span className="material-symbols-outlined" style={{ fontSize: "18px" }}>play_circle</span> {t("Trigger Now")}</>
            )}
          </button>

          {triggerResult && (
            <small className={`d-flex align-items-center gap-1 ${triggerResult.success ? "text-success" : "text-danger"}`}>
              <span className="material-symbols-outlined" style={{ fontSize: "16px" }}>
                {triggerResult.success ? "check_circle" : "error"}
              </span>
              {triggerResult.message}
            </small>
          )}

          {/* Save — saves webhook_url, method, token, payload_template for all subtasks */}
          <button
            type="button"
            className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1 ms-auto"
            disabled={saving || !!payloadJsonError || !task}
            onClick={handleSave}
          >
            {saving
              ? <><span className="spinner-border spinner-border-sm" /> {t("Saving…")}</>
              : <><span className="material-symbols-outlined" style={{ fontSize: "16px" }}>save</span> {t("Save")}</>}
          </button>

          {saveResult && (
            <small className={`d-flex align-items-center gap-1 ${saveResult.success ? "text-success" : "text-danger"}`}>
              <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>
                {saveResult.success ? "check_circle" : "error"}
              </span>
              {saveResult.message}
            </small>
          )}
        </div>
      </div>

      {/* Backdrop */}
      <div className="offcanvas-backdrop fade show" onClick={handleClose} />
    </>
  );
};

export default AutomationTaskDetailPopup;
